import { useComprehensiveTopNav } from 'hooks';
import { routes } from 'lib/api';
import { Button, Image, LoadingIndicator, Typography } from 'lib/components';
import { getLocation, PermissionType as P, JobStatus } from 'lib/util';
import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Redirect, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import Authorized from 'routes/authorized';
import {
	actions,
	RequestManager,
	useAppDispatch,
	useSelectAuthorization,
	useSelectHospital,
	useSelectRequest,
	useSelectSettings,
	useSelectJobs,
} from 'store';
import { Hospital } from 'types';
import adjustTime from '../../lib/adjustTime';
import EmployeesTab from './EmployeesTab';
import classes from './index.module.scss';
import InvoicesTab from './InvoicesTab';
import JobsTab from './JobsTab';
import ItemsTab from './RestrictedItemsTab';
import ServicesTab from './ServicesTab';
import VendorsTab from './VendorsTab';

/**
 * Props interface for the HospitalDetailScreenView component
 */
interface Props {
	/** Hospital object containing all hospital data */
	hospital: Hospital;
}

/**
 * HospitalDetailScreen component
 * Wrapper component that fetches hospital data by ID from URL params
 * Renders the detailed hospital view when data is available
 *
 * @returns {JSX.Element} - Rendered component
 */
const HospitalDetailScreen = () => {
	// Get hospital ID from URL parameters
	const { id } = useParams<{ id: string }>();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Active API request tracking
	const { active } = useSelectRequest();
	// Selector function to get a hospital by ID
	const getHospitalById = useSelectHospital();

	// Store the selected hospital data
	const [hospital, setHospital] = useState<Hospital>();

	/**
	 * Fetch hospital data and BuddyPunch locations on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchHospital(parseInt(id)));
		dispatch(actions.getBuddyPunchLocations());
	}, []);

	/**
	 * Monitor API requests for hospital data
	 * Updates local hospital state when data is successfully fetched
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.HOSPITAL_ID(parseInt(id)), 'GET')) {
			if (RM.succeeded()) {
				const hospital = getHospitalById(parseInt(id));

				if (hospital) {
					setHospital(hospital);
				}
			}
		}
	}, [active]);

	// Render hospital view if data is available, otherwise show loading indicator
	return hospital ? <HospitalDetailScreenView hospital={hospital} /> : <LoadingIndicator />;
};

/**
 * HospitalDetailScreenView component
 * Detailed view for a hospital with tabs for different sections (jobs, employees, etc.)
 * Displays hospital information and provides navigation to different tabs
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const HospitalDetailScreenView = ({ hospital }: Props) => {
	// Application settings from Redux store
	const settingsState = useSelectSettings();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Jobs data from Redux store
	const jobs = useSelectJobs();

	// Navigation helpers
	const { push } = useHistory();
	// Route matching for nested routes
	const { url, path } = useRouteMatch();

	// Permission checks for various hospital-related actions
	const { canUpdateHospital, canListJob, canListEmployee, canListInvoice, canListVendor, canListService } =
		useSelectAuthorization();

	/**
	 * Fetch jobs for this hospital if user has permission to view jobs
	 */
	useEffect(() => {
		if (canListJob) {
			dispatch(actions.fetchJobsForHospital(hospital.id));
		}
	}, [canListJob]);

	/**
	 * Determine which tab to redirect to based on user permissions
	 * Returns the URL for the first tab the user has permission to view
	 *
	 * @returns {string} - URL to redirect to, or empty string if no permissions
	 */
	const redirect = () => {
		if (canListJob) return `${url}/jobs`;
		if (canListEmployee) return `${url}/employees`;
		if (canListVendor) return `${url}/vendors`;
		if (canListInvoice) return `${url}/invoices`;
		if (canListService) return `${url}/services`;
		return '';
	};

	// Set up the top navigation bar with hospital name
	useComprehensiveTopNav(`${hospital.name} - Profile Page`);

	/**
	 * Handle phone call button click
	 * Creates and clicks a tel: link to initiate a phone call
	 */
	const handleCallButtonClick = useCallback(() => {
		const link = document.createElement('a');
		link.href = `tel:${hospital.phoneNumber}`;
		link.click();
	}, [hospital.id]);

	/**
	 * Handle email button click
	 * Creates and clicks a mailto: link to open email client
	 */
	const handleEmailButtonClick = useCallback(() => {
		const link = document.createElement('a');
		link.href = `mailto:${hospital.email}`;
		link.click();
	}, [hospital.id]);

	return (
		<div className={classes['hospitalDetail']}>
			{/* Hospital header with name, location, and edit button */}
			<div
				className={classes['row']}
				style={{
					justifyContent: 'space-between',
					paddingBottom: '0.5rem',
				}}
			>
				{/* Hospital name and location */}
				<div
					style={{
						marginTop: '0.5rem',
					}}
				>
					{/* Hospital name */}
					<Typography variant="h4">{hospital.name}</Typography>
					{/* Hospital location with icon */}
					<div
						className={classes['row']}
						style={{
							marginTop: '0.5rem',
						}}
					>
						<Image src={'icLocationSmGray'} />
						<Typography color="gray">{getLocation(hospital.address)}</Typography>
					</div>
				</div>
				{/* Edit button - only shown if user has permission to update hospital */}
				{canUpdateHospital && (
					<Button
						className={classes['btn']}
						title="Edit"
						variant="standard"
						start={<Image src="icEdit" />}
						end={<Image src="icDownCaretSm" />}
						onClick={() => push(`/hospitals/edit/${hospital.id}`)}
					/>
				)}
			</div>
			{/* Hospital statistics and contact buttons row */}
			<div
				className={classes['row']}
				style={{
					justifyContent: 'space-between',
				}}
			>
				{/* Hospital statistics (registration date, employee count, active jobs) */}
				<div className={classes['row']}>
					{/* Registration date */}
					<Typography
						color="gray"
						style={{
							marginRight: '0.75rem',
						}}
					>
						Registered: &nbsp;
						<em>{adjustTime(settingsState, hospital.createdAt).format('MM/DD/YYYY')}</em>
					</Typography>
					{/* Employee count */}
					<Typography
						color="gray"
						style={{
							marginRight: '0.75rem',
						}}
					>
						Employees: &nbsp;
						<em>{hospital.employeesAmount}</em>
					</Typography>
					{/* Active jobs count - only shown if user has permission to view jobs */}
					{canListJob && (
						<Typography color="gray">
							Active Jobs: &nbsp;
							<em> {jobs.list.filter((job) => JobStatus.isActive(job.status)).length} Active</em>
						</Typography>
					)}
				</div>
				{/* Contact buttons (call and email) */}
				<div className={classes['row']}>
					{/* Call button - only shown if hospital has a phone number */}
					{hospital.phoneNumber !== undefined && hospital.phoneNumber !== null && (
						<Button
							onClick={handleCallButtonClick}
							title="Call"
							variant="standard"
							end={
								<Image
									style={{
										width: '1rem',
										height: '1rem',
									}}
									src="icTelephonePrimary"
								/>
							}
							style={{
								margin: '0.5rem 0.5rem 0 0',
							}}
							className={classes['btn']}
						/>
					)}
					{/* Email button - only shown if hospital has an email */}
					{hospital.email !== undefined && hospital.email !== null && (
						<Button
							onClick={handleEmailButtonClick}
							title="Email"
							variant="standard"
							end={
								<Image
									style={{
										width: '1rem',
										height: '1rem',
									}}
									src="icMailPrimary"
								/>
							}
							style={{
								margin: '0.5rem 0 0 0',
							}}
							className={classes['btn']}
						/>
					)}
				</div>
			</div>
			{/* Tabbed interface for hospital details */}
			<div className={classes['hospitalDetail-tabs']}>
				{/* Tab navigation header */}
				<div className={classes['hospitalDetail-tabs-top']}>
					<div className={classes['hospitalDetail-tabs-headers']}>
						{/* Jobs tab - only shown if user has permission */}
						{canListJob && (
							<NavLink
								to={`${url}/jobs`}
								activeClassName={classes['hospitalDetail-tabs-link-active']}
								className={classes['hospitalDetail-tabs-link']}
							>
								<Typography
									className={
										classes['hospitalDetail-tabs-headerTitle'] +
										' ' +
										classes['hospitalDetail-tabs-headerTitle-active']
									}
								>
									Jobs
								</Typography>
							</NavLink>
						)}
						{/* Employees tab - only shown if user has permission */}
						{canListEmployee && (
							<NavLink
								to={`${url}/employees`}
								activeClassName={classes['hospitalDetail-tabs-link-active']}
								className={classes['hospitalDetail-tabs-link']}
							>
								<Typography className={classes['hospitalDetail-tabs-headerTitle']}>
									Employees
								</Typography>
							</NavLink>
						)}
						{/* Restricted Vendors tab - only shown if user has permission */}
						{canListVendor && (
							<NavLink
								to={`${url}/vendors`}
								activeClassName={classes['hospitalDetail-tabs-link-active']}
								className={classes['hospitalDetail-tabs-link']}
							>
								<Typography className={classes['hospitalDetail-tabs-headerTitle']}>
									Restricted Vendors
								</Typography>
							</NavLink>
						)}
						{/* Restricted Items tab - available to all users */}
						<NavLink
							to={`${url}/items`}
							activeClassName={classes['hospitalDetail-tabs-link-active']}
							className={classes['hospitalDetail-tabs-link']}
						>
							<Typography className={classes['hospitalDetail-tabs-headerTitle']}>
								Restricted Items
							</Typography>
						</NavLink>
						{/* Services tab - only shown if user has permission */}
						{canListService && (
							<NavLink
								to={`${url}/services`}
								activeClassName={classes['hospitalDetail-tabs-link-active']}
								className={classes['hospitalDetail-tabs-link']}
							>
								<Typography className={classes['hospitalDetail-tabs-headerTitle']}>Services</Typography>
							</NavLink>
						)}
						{/* Invoices tab - only shown if user has permission */}
						{canListInvoice && (
							<NavLink
								to={`${url}/invoices`}
								activeClassName={classes['hospitalDetail-tabs-link-active']}
								className={classes['hospitalDetail-tabs-link']}
							>
								<Typography className={classes['hospitalDetail-tabs-headerTitle']}>Invoices</Typography>
							</NavLink>
						)}
					</div>
				</div>
				{/* Tab content area */}
				<div className={classes['hospitalDetail-tabs-body']}>
					<Switch>
						{/* Each route is protected by permission check */}
						{/* Jobs tab content */}
						<Authorized permission={P.LIST_JOB} exact path={`${path}/jobs`} component={JobsTab} />
						{/* Employees tab content */}
						<Authorized
							permission={P.LIST_EMPLOYEE}
							exact
							path={`${path}/employees`}
							component={EmployeesTab}
						/>
						{/* Vendors tab content */}
						<Authorized permission={P.LIST_VENDOR} exact path={`${path}/vendors`} component={VendorsTab} />
						{/* Items tab content */}
						<Authorized permission={P.LIST_VENDOR} exact path={`${path}/items`} component={ItemsTab} />
						{/* Services tab content */}
						<Authorized
							permission={P.LIST_SERVICE}
							exact
							path={`${path}/services`}
							component={ServicesTab}
						/>
						{/* Invoices tab content */}
						<Authorized
							permission={P.LIST_INVOICE}
							exact
							path={`${path}/invoices`}
							component={InvoicesTab}
						/>
						{/* Default redirect to first available tab */}
						{redirect() !== '' && <Redirect from="/" to={redirect()} />}
					</Switch>
				</div>
			</div>
		</div>
	);
};

export default HospitalDetailScreen;
